import { authFetch } from './api.js';
import { registerViewLoader } from './navigation.js';
import { getUserRole } from './state.js';
import { showToast } from './utils.js';

function apiPath() {
  return getUserRole() === 'admin'
    ? '/api/admin/ad-optimization/rules'
    : '/api/v1/ad-optimization/rules';
}

function isAdminMode() {
  return getUserRole() === 'admin';
}

const DEFAULT_TEMPLATE = {
  id: null,
  configName: '新广告规则模板',
  isDefault: false,
  acosHighPercent: 30,
  acoasRiskPercent: 18,
  ctrLowPercent: 0.3,
  cvrLowPercent: 5,
  cvrPromotePercent: 20,
  clickNoOrderThreshold: 30,
  lowCvrClickThreshold: 30,
  inventoryHighRiskDays: 15,
  enableAi: true,
  updatedAt: null,
};

let rules = [];
let activeId = null;
let isCreating = false;

const FIELD_IDS = [
  'ad-rule-config-name',
  'ad-rule-acos-high',
  'ad-rule-acoas-risk',
  'ad-rule-ctr-low',
  'ad-rule-cvr-low',
  'ad-rule-cvr-promote',
  'ad-rule-click-no-order',
  'ad-rule-low-cvr-click',
  'ad-rule-inventory-days',
  'ad-rule-enable-ai',
];

export function initAdRuleManagement() {
  registerViewLoader('admin-ad-rules', () => loadAdRules());
  document.getElementById('ad-rules-refresh-btn')?.addEventListener('click', () => loadAdRules());
  document.getElementById('ad-rules-new-btn')?.addEventListener('click', () => openCreateModal());
  document.getElementById('ad-rules-empty-create-btn')?.addEventListener('click', () => openCreateModal());
  document.getElementById('ad-rules-modal-close')?.addEventListener('click', closeRuleModal);
  document.getElementById('ad-rules-modal-cancel')?.addEventListener('click', closeRuleModal);
  document.getElementById('ad-rules-modal')?.addEventListener('click', (event) => {
    if (event.target?.id === 'ad-rules-modal') closeRuleModal();
  });
  document.getElementById('ad-rules-form')?.addEventListener('submit', (event) => {
    event.preventDefault();
    saveActiveRule();
  });
  document.getElementById('ad-rules-set-default-btn')?.addEventListener('click', () => {
    if (activeId) setRuleDefault(activeId);
  });
  document.getElementById('ad-rules-delete-btn')?.addEventListener('click', deleteActiveRule);
}

function setStatus(text) {
  const el = document.getElementById('ad-rules-status');
  if (el) el.textContent = text || '';
}

function activeRule() {
  if (isCreating) return { ...DEFAULT_TEMPLATE };
  return rules.find((rule) => String(rule.id) === String(activeId)) || { ...DEFAULT_TEMPLATE };
}

function numberValue(id, fallback = 0) {
  const raw = document.getElementById(id)?.value;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

function readForm() {
  return {
    configName: document.getElementById('ad-rule-config-name')?.value.trim() || DEFAULT_TEMPLATE.configName,
    acosHighPercent: numberValue('ad-rule-acos-high', DEFAULT_TEMPLATE.acosHighPercent),
    acoasRiskPercent: numberValue('ad-rule-acoas-risk', DEFAULT_TEMPLATE.acoasRiskPercent),
    ctrLowPercent: numberValue('ad-rule-ctr-low', DEFAULT_TEMPLATE.ctrLowPercent),
    cvrLowPercent: numberValue('ad-rule-cvr-low', DEFAULT_TEMPLATE.cvrLowPercent),
    cvrPromotePercent: numberValue('ad-rule-cvr-promote', DEFAULT_TEMPLATE.cvrPromotePercent),
    clickNoOrderThreshold: numberValue('ad-rule-click-no-order', DEFAULT_TEMPLATE.clickNoOrderThreshold),
    lowCvrClickThreshold: numberValue('ad-rule-low-cvr-click', DEFAULT_TEMPLATE.lowCvrClickThreshold),
    inventoryHighRiskDays: numberValue('ad-rule-inventory-days', DEFAULT_TEMPLATE.inventoryHighRiskDays),
    enableAi: Boolean(document.getElementById('ad-rule-enable-ai')?.checked),
  };
}

function fillForm(rule) {
  const current = rule || activeRule();
  const setValue = (id, value) => {
    const el = document.getElementById(id);
    if (el) el.value = value ?? '';
  };

  setValue('ad-rule-config-name', current.configName || '');
  setValue('ad-rule-acos-high', current.acosHighPercent ?? '');
  setValue('ad-rule-acoas-risk', current.acoasRiskPercent ?? '');
  setValue('ad-rule-ctr-low', current.ctrLowPercent ?? '');
  setValue('ad-rule-cvr-low', current.cvrLowPercent ?? '');
  setValue('ad-rule-cvr-promote', current.cvrPromotePercent ?? '');
  setValue('ad-rule-click-no-order', current.clickNoOrderThreshold ?? '');
  setValue('ad-rule-low-cvr-click', current.lowCvrClickThreshold ?? '');
  setValue('ad-rule-inventory-days', current.inventoryHighRiskDays ?? '');

  const ai = document.getElementById('ad-rule-enable-ai');
  if (ai) ai.checked = Boolean(current.enableAi);
}

function formatDateText(value) {
  if (!value) return '未记录';
  return String(value).slice(0, 10);
}

function ruleSummary(rule) {
  if (rule.isDefault) {
    return '适用于大多数商品的基础广告优化规则，平衡曝光量与广告成本，适合日常运营使用。';
  }
  if ((rule.acosHighPercent || 0) >= 40) {
    return '适用于新品推广期或旺季冲刺场景，允许更高的 ACOS 上限以换取更多曝光和转化。';
  }
  if ((rule.enableAi)) {
    return '适用于需要 AI 洞察辅助判断的投放场景，帮助更快筛出异常与机会词。';
  }
  return '用于管理广告优化判断阈值，统一团队的投放分析口径。';
}

function createAdRuleElement(tagName, className = '', text = '') {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== '') element.textContent = text;
  return element;
}

function createAdRuleMetric(label, value) {
  const metric = createAdRuleElement('span');
  metric.append(`${label} `);
  metric.appendChild(createAdRuleElement('strong', '', value));
  return metric;
}

function createAdRuleCard(rule) {
  const article = createAdRuleElement('article', 'ad-rule-showcase-card');
  const head = createAdRuleElement('div', 'ad-rule-showcase-head');
  const titleWrap = createAdRuleElement('div', 'ad-rule-showcase-title-wrap');
  titleWrap.appendChild(createAdRuleElement('h3', 'ad-rule-showcase-title', rule.configName || '未命名模板'));
  titleWrap.appendChild(createAdRuleElement(
    'span',
    rule.isDefault ? 'ad-rule-chip ad-rule-chip-default' : 'ad-rule-chip ad-rule-chip-muted',
    rule.isDefault ? '默认' : '备用',
  ));
  head.append(
    titleWrap,
    createAdRuleElement('span', 'ad-rule-showcase-date', `创建时间 ${formatDateText(rule.updatedAt)}`),
  );

  const metrics = createAdRuleElement('div', 'ad-rule-showcase-metrics');
  metrics.append(
    createAdRuleMetric('ACOS 阈值', `≥${rule.acosHighPercent ?? 0}%`),
    createAdRuleMetric('曝光阈值', `≤${rule.clickNoOrderThreshold ?? 0}`),
    createAdRuleMetric('CVR 阈值', `≤${rule.cvrLowPercent ?? 0}%`),
  );

  const actions = createAdRuleElement('div', 'ad-rule-showcase-actions');
  const editButton = createAdRuleElement('button', 'btn btn-secondary ad-rule-action-btn', '编辑');
  editButton.type = 'button';
  editButton.dataset.role = 'edit';
  editButton.dataset.ruleId = String(rule.id ?? '');
  editButton.addEventListener('click', () => openEditModal(rule.id));
  const defaultButton = createAdRuleElement(
    'button',
    'btn btn-primary ad-rule-action-btn',
    rule.isDefault ? '当前默认' : '设为默认',
  );
  defaultButton.type = 'button';
  defaultButton.dataset.role = 'default';
  defaultButton.dataset.ruleId = String(rule.id ?? '');
  defaultButton.disabled = Boolean(rule.isDefault);
  defaultButton.addEventListener('click', () => setRuleDefault(rule.id));
  actions.append(editButton, defaultButton);

  article.append(
    head,
    createAdRuleElement('p', 'ad-rule-showcase-copy', ruleSummary(rule)),
    metrics,
    actions,
  );
  return article;
}

function renderRules() {
  const host = document.getElementById('ad-rules-list');
  const empty = document.getElementById('ad-rules-empty');
  const count = document.getElementById('ad-rules-count');
  if (!host) return;

  if (count) count.textContent = `已加载 ${rules.length} 套模板`;

  if (!rules.length) {
    host.replaceChildren();
    if (empty) empty.hidden = false;
    return;
  }

  if (empty) empty.hidden = true;
  host.replaceChildren(...rules.map(createAdRuleCard));
}

async function requestJson(url, options = {}) {
  const res = await authFetch(url, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

export async function loadAdRules() {
  setStatus('加载中...');
  try {
    const data = await requestJson(apiPath());
    rules = Array.isArray(data.items) ? data.items : [];
    activeId = data.activeRuleConfigId ?? rules[0]?.id ?? null;
    isCreating = false;
    renderRules();
    setStatus(`已同步 ${rules.length} 套规则模板`);
  } catch (error) {
    console.error(error);
    const host = document.getElementById('ad-rules-list');
    if (host) host.replaceChildren();
    const empty = document.getElementById('ad-rules-empty');
    if (empty) {
      empty.hidden = false;
      empty.querySelector('p').textContent = '加载失败，请刷新后重试';
    }
    setStatus('加载失败');
    showToast(error.message || '加载广告规则失败', 'error');
  }
}

function toggleModalReadonly(disabled) {
  FIELD_IDS.forEach((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (id === 'ad-rule-enable-ai') {
      el.disabled = Boolean(disabled);
    } else {
      el.toggleAttribute('disabled', Boolean(disabled));
    }
  });
}

function updateModalState(rule) {
  const title = document.getElementById('ad-rules-editor-title');
  const badge = document.getElementById('ad-rules-default-badge');
  const saveBtn = document.getElementById('ad-rules-save-btn');
  const deleteBtn = document.getElementById('ad-rules-delete-btn');
  const defaultBtn = document.getElementById('ad-rules-set-default-btn');
  const helper = document.getElementById('ad-rules-modal-helper');
  const readOnlyGlobal = !isAdminMode() && !isCreating && rule.scopeType === 'GLOBAL';

  if (title) title.textContent = isCreating ? '新建模板' : '编辑模板';
  if (badge) badge.hidden = !(rule.isDefault && !isCreating);
  if (helper) {
    helper.textContent = isCreating
      ? '填写规则阈值后保存，即可创建新的广告规则模板。'
      : '字段名对应规则含义，百分比字段请直接填写 30 表示 30%。';
  }

  toggleModalReadonly(readOnlyGlobal);
  saveBtn?.toggleAttribute('disabled', readOnlyGlobal);
  defaultBtn?.toggleAttribute('disabled', isCreating || rule.isDefault || readOnlyGlobal);
  deleteBtn?.toggleAttribute('disabled', isCreating || readOnlyGlobal || (isAdminMode() && rules.length <= 1));

  if (defaultBtn) defaultBtn.textContent = isAdminMode() ? '设为系统默认' : '设为个人默认';
}

function openRuleModal(rule) {
  fillForm(rule);
  updateModalState(rule);
  const modal = document.getElementById('ad-rules-modal');
  if (modal) modal.hidden = false;
  document.body.classList.add('ad-rule-modal-open');
  document.getElementById('ad-rule-config-name')?.focus();
}

function closeRuleModal() {
  const modal = document.getElementById('ad-rules-modal');
  if (modal) modal.hidden = true;
  document.body.classList.remove('ad-rule-modal-open');
  isCreating = false;
}

function openCreateModal() {
  isCreating = true;
  activeId = null;
  openRuleModal({
    ...DEFAULT_TEMPLATE,
    configName: `广告规则模板 ${rules.length + 1}`,
  });
}

function openEditModal(ruleId) {
  const rule = rules.find((item) => String(item.id) === String(ruleId));
  if (!rule) {
    showToast('未找到要编辑的模板', 'error');
    return;
  }
  isCreating = false;
  activeId = rule.id;
  openRuleModal(rule);
}

async function saveActiveRule() {
  const payload = readForm();
  try {
    if (isCreating || !activeId) {
      const created = await requestJson(apiPath(), {
        method: 'POST',
        body: JSON.stringify({ ...payload, isDefault: rules.length === 0 }),
      });
      activeId = created.id;
      showToast('模板已创建', 'success');
    } else {
      const updated = await requestJson(`${apiPath()}/${encodeURIComponent(activeId)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      activeId = updated.id;
      showToast('模板已保存', 'success');
    }
    closeRuleModal();
    await loadAdRules();
  } catch (error) {
    showToast(error.message || '保存模板失败', 'error');
  }
}

async function setRuleDefault(ruleId) {
  if (!ruleId) return;
  try {
    const rule = await requestJson(`${apiPath()}/${encodeURIComponent(ruleId)}/default`, { method: 'POST' });
    activeId = rule.id;
    await loadAdRules();
    showToast(isAdminMode() ? '系统默认模板已更新' : '默认模板已更新', 'success');
    if (document.getElementById('ad-rules-modal')?.hidden === false) {
      const active = rules.find((item) => String(item.id) === String(activeId));
      if (active) openRuleModal(active);
    }
  } catch (error) {
    showToast(error.message || '设置默认失败', 'error');
  }
}

async function deleteActiveRule() {
  if (!activeId || (isAdminMode() && rules.length <= 1)) return;
  const rule = activeRule();
  if (!window.confirm(`确认删除「${rule.configName || '未命名模板'}」？`)) return;
  try {
    const result = await requestJson(`${apiPath()}/${encodeURIComponent(activeId)}`, { method: 'DELETE' });
    activeId = result.defaultRuleConfigId || null;
    closeRuleModal();
    await loadAdRules();
    showToast('模板已删除', 'success');
  } catch (error) {
    showToast(error.message || '删除模板失败', 'error');
  }
}
